import React, { useRef } from 'react';

/**
 * Grupo de abas com semântica WAI-ARIA de `tablist` (at-56, frente B).
 *
 * Porta o comportamento que a at-38 já tinha implementado à mão em `/adesoes` —
 * **roving tabindex**: o Tab entra no grupo pela aba ativa e as setas ← → andam
 * entre elas, com Home/End nas extremidades. Estava correto lá; o que faltava era
 * não precisar reescrevê-lo na próxima tela (`/ti/envios` tem abas próprias, e o
 * item 29 do backlog já decidiu que o histórico da carteira entra **como aba
 * desta tela** — o grupo vai crescer).
 *
 * ⚠️ **O contador vai no `rotulo`, montado pelo consumidor** ("Em andamento (3)").
 * O componente não conta nada: na spec 02 §3.3 aquele número é global do
 * consultor, não muda com busca nem com filtro, e é buscado antes de a aba abrir
 * — regra de domínio, que não desce para `src/ui`.
 *
 * ⚠️ **A troca de aba não pode passar por loader de tela cheia** (spec 02 §4):
 * *"é requisito que a troca seja instantânea"*. Por isso este componente não tem
 * estado de carregamento — quem carrega é o painel.
 *
 * O painel é renderizado pelo consumidor, com os ids que `idDaAba`/`idDoPainel`
 * devolvem — é o que amarra `aria-controls` e `aria-labelledby` dos dois lados.
 */

export interface AbaDefinicao<Id extends string = string> {
  id: Id;
  rotulo: string;
}

/** Id do botão de uma aba. Use no `aria-labelledby` do painel. */
export const idDaAba = (base: string, id: string): string => `${base}-aba-${id}`;
/** Id do painel. Use no `aria-controls` — já aplicado nos botões. */
export const idDoPainel = (base: string): string => `${base}-painel`;

interface PropsTabs<Id extends string> {
  /** Prefixo dos ids de acessibilidade (ex.: `"adesoes"`). */
  idBase: string;
  abas: AbaDefinicao<Id>[];
  ativa: Id;
  aoTrocar: (id: Id) => void;
  /** Descreve o grupo para o leitor de tela (ex.: "Seções das adesões"). */
  rotuloAcessivel: string;
}

const Tabs = <Id extends string>({
  idBase,
  abas,
  ativa,
  aoTrocar,
  rotuloAcessivel,
}: PropsTabs<Id>) => {
  const botoes = useRef<Array<HTMLButtonElement | null>>([]);

  const aoTeclar = (evento: React.KeyboardEvent<HTMLDivElement>) => {
    const atual = abas.findIndex((aba) => aba.id === ativa);
    let alvo = atual;

    if (evento.key === 'ArrowRight') alvo = (atual + 1) % abas.length;
    else if (evento.key === 'ArrowLeft') alvo = (atual - 1 + abas.length) % abas.length;
    else if (evento.key === 'Home') alvo = 0;
    else if (evento.key === 'End') alvo = abas.length - 1;
    else return;

    evento.preventDefault();
    aoTrocar(abas[alvo].id);
    botoes.current[alvo]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={rotuloAcessivel}
      onKeyDown={aoTeclar}
      /*
       * ⚠️ **Sem `overflow-x-auto`.** A borda inferior é o que une as abas ao
       * conteúdo, e um contexto de rolagem aqui corta esse traço e pode desenhar
       * uma barra horizontal — que nesta interface nunca deve aparecer. Se um dia
       * houver abas demais, a saída é encurtar rótulo, não rolar.
       */
      className="tw-flex tw-gap-0 tw-border-b tw-border-superficie-borda"
    >
      {abas.map((aba, indice) => {
        const selecionada = aba.id === ativa;
        return (
          <button
            key={aba.id}
            type="button"
            id={idDaAba(idBase, aba.id)}
            role="tab"
            aria-selected={selecionada}
            aria-controls={idDoPainel(idBase)}
            /* roving tabindex: só a aba ativa é alcançável por Tab. */
            tabIndex={selecionada ? 0 : -1}
            ref={(elemento) => {
              botoes.current[indice] = elemento;
            }}
            onClick={() => aoTrocar(aba.id)}
            className={[
              'tw-relative tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent',
              /* `px-5 py-3.5` é a medida do protótipo, e já dá 48px de altura —
                 acima dos 44px de alvo de toque da spec 01 §7, sem `min-h`. */
              /* Peso `medium` (500), sem `font-marca`: o protótipo reserva a fonte
                 da marca (Nunito) ao título da tela, e a aba não deve competir com
                 ele (Victor, 09/09/2026: "abas também não devem ficar em negrito").
                 Quem marca a aba ativa é a COR e o sublinhado, não o peso. */
              'tw-px-5 tw-py-3.5 tw-text-sm tw-font-medium tw-transition-colors',
              'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-brand/40',
              'motion-reduce:tw-transition-none',
              selecionada ? 'tw-text-brand' : 'tw-text-texto-suave hover:tw-text-texto-corpo',
            ].join(' ')}
          >
            {aba.rotulo}
            {/* Sublinhado da aba ativa. `aria-hidden` porque `aria-selected` já
                informa o estado — o traço é redundância visual, não semântica. */}
            {selecionada && (
              <span
                className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-0.5 tw-rounded-t-full tw-bg-brand"
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
